import classnames from "classnames";
import { uniqueId } from "lodash";
import React, { FC, JSX, ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { TooltipPlacement } from "./literals";
import Portal from "./Portal";
import { commonFocusableElements } from "./utility";

interface TooltipProps {
  children?: ReactNode;
  containerClassName?: string;
  /**
   * This is to be used if the tooltip is inside a flyout. It should be given the flyout's id,
   * so that clicking inside the tooltip does not close the flyout.
   */
  containerId?: string;
  delay?: number;
  position?: TooltipPlacement;
  style?: React.CSSProperties;
  text: string | JSX.Element;
  triggerWidth?: string;
  tooltipWidth?: string;
  sticky?: boolean;
  oneLine?: boolean;
}

interface Coords {
  x: number;
  y: number;
}

const TooltipWrapper: FC<React.PropsWithChildren<TooltipProps>> = (props: TooltipProps) => {
  const showTimeout = useRef<number>(null);
  const unRenderTimeout = useRef<number>(null);
  const isMousedOver = useRef(false);
  const isFocused = useRef(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipId = useRef(uniqueId());
  const container = useRef(document.getElementById(props.containerId)).current;
  const [coordinates, setCoordinates] = useState<Coords>({ x: 0, y: 0 });
  const [shouldRender, setShouldRender] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [shouldHaveTabIndex, setShouldHaveTabIndex] = useState(true);

  const containerClass = useMemo(() => {
    return !!props.containerClassName ? `${props.containerClassName} tooltip-container` : "tooltip-container";
  }, [props.containerClassName]);

  useEffect(() => {
    if (shouldRender && !!tooltipRef.current) {
      showTimeout.current = window.setTimeout(() => {
        const newCoords = getRenderedCoords(tooltipRef.current);
        if (newCoords) {
          setCoordinates(newCoords);
          setShouldShow(true);
        }
      }, 100 + (props.delay || 0));
    }
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldShow && !!tooltipRef.current) {
      window.setTimeout(() => {
        setShouldRender(false);
      }, 300); // allows time for the css transition
    }
  }, [shouldShow]);

  useEffect(() => {
    const event = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        unRenderTooltip();
        if (isFocused.current) {
          (document.activeElement as HTMLElement).blur();
          isFocused.current = false;
        }
        window.removeEventListener("keyup", event);
      }
    };
    if (shouldShow) {
      // for some reason "Escape" doesn't trigger keypress
      window.addEventListener("keyup", event);
    }

    return () => {
      window.removeEventListener("keyup", event);
    }
  }, [shouldShow]);

  // This will remove an unnecessary tabindex attribute from the 
  // tooltip trigger span if props.children includes buttons, a, inputs, etc.
  // We have to rerun it on children updates because they _can_ change,
  // e.g. changing the disabled attribute on a button
  useEffect(() => {
    setShouldHaveTabIndex(!triggerRef.current?.querySelector(commonFocusableElements));
  }, [props.children]);

  const getRenderedCoords = (el: Element) => {
    if (!el) { return; } // in case the element has unmounted before the timeout called this
    const tRect = el.getBoundingClientRect();
    switch (props.position) {
      case "left":
        return {
          x: coordinates.x - tRect.width,
          y: coordinates.y - tRect.height / 2,
        };
      case "right":
        return {
          x: coordinates.x,
          y: coordinates.y - tRect.height / 2,
        };
      case "top":
      case "bottom":
      default:
        return coordinates;
    }
  }

  const handleMouseOver = (el: Element) => {
    window.clearTimeout(unRenderTimeout.current);
    unRenderTimeout.current = null;
    const rect = (el).closest(".tooltip-trigger")?.getBoundingClientRect();
    if (!rect) {
      unRenderTooltip();
      return;
    }

    isMousedOver.current = true;
    if (showTimeout.current != null) {
      return;
    }
    renderTooltip(rect);
  }

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    const rect = (e.target as Element).closest(".tooltip-trigger")?.getBoundingClientRect();
    if (!rect) {
      unRenderTooltip();
      return;
    }
    isFocused.current = true;
    if (showTimeout.current != null) {
      return;
    }
    renderTooltip(rect);
  }

  const handleMouseLeave = () => {
    isMousedOver.current = false;
    if (isFocused.current) {
      return;
    }
    unRenderTimeout.current = window.setTimeout(() => {
      unRenderTooltip();
    }, 250);
  }

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    isFocused.current = false;
    if (isMousedOver.current || e.relatedTarget?.classList.contains("tooltip-body")) {
      return;
    }
    unRenderTooltip();
  }

  const renderTooltip = (rect: DOMRect) => {
    switch (props.position) {
      case "left":
        // we also subtract the tooltip's width and height in a function above
        setCoordinates({
          x: rect.left + window.scrollX - 16,
          y: rect.top + window.scrollY + (rect.height / 2),
        });
        setShouldRender(true);
        break;
      case "right":
        // we also subtract the tooltip's height in a function above
        setCoordinates({
          x: rect.right + window.scrollX + 16,
          y: rect.top + window.scrollY + (rect.height / 2),
        });
        setShouldRender(true);
        break;
      case "bottom":
        setCoordinates({
          x: rect.left + window.scrollX + (rect.width / 2),
          y: rect.bottom + window.scrollY + 16,
        });
        setShouldRender(true);
        break;
      case "top":
      default:
        setCoordinates({
          x: rect.left + window.scrollX + (rect.width / 2),
          y: rect.top + window.scrollY - 16,
        });
        setShouldRender(true);
        break;
    }
  }

  const unRenderTooltip = () => {
    if (shouldShow) {
      if (showTimeout.current != null) {
        window.clearTimeout(showTimeout.current);
        showTimeout.current = null;
      }
      // only set show to false so we can transition out
      setShouldShow(false);
    } else {
      if (showTimeout.current != null) {
        if (props.sticky)
          return;

        window.clearTimeout(showTimeout.current);
        showTimeout.current = null;
      }
      // mouseLeave must have triggered early
      setShouldRender(false);
      setShouldShow(false);
    }
  }

  return (
    !props.text ? <>{props.children}</> :
      <span className={containerClass} style={props.style}>
        {shouldRender && !!props.text ?
          (<Portal hideBodyScroll={false} container={container}>
            <div
              className={classnames(
                "tooltip-body",
                "tooltip-portal",
                {
                  [props.position]: !!props.position,
                  show: shouldShow,
                },
              )}
              ref={tooltipRef}
              style={{
                display: "block",
                left: `${coordinates.x}px`,
                top: `${coordinates.y}px`,
                whiteSpace: props.oneLine ? "nowrap" : "normal",
                width: props.tooltipWidth || "auto",
              }}
              id={tooltipId.current}
              aria-hidden={!shouldShow}
              onMouseOver={() => handleMouseOver(triggerRef.current)}
              onMouseLeave={handleMouseLeave}
            >
              {props.text}
              <div className="tooltip-arrow" />
            </div>
          </Portal>)
          : null
        }
        <span
          className="tooltip-trigger"
          ref={triggerRef}
          onBlur={handleBlur}
          onFocus={handleFocus} // focus and blur allow keyboard control to show/hide the tooltip
          onMouseLeave={handleMouseLeave}
          onMouseOver={(e) => handleMouseOver(e.target as HTMLElement)}
          style={{ width: props.triggerWidth || "auto" }}
          aria-describedby={tooltipId.current}
          // makes the element keyboard-focusable in a default order
          // accessibility recommendations say tooltips should only really be on interactive elements like buttons, 
          // but we have a ton that just describe truncated words and things like that, and those elements
          // aren't focusable by default
          // However, if the child _is_ interactive, we don't want it to get tab focus because then the trigger and the 
          // child will both show up in tab order. We can't know what HTML elements are contained in props.children,
          // so we have a useEffect set up to look at it after render. As a result, we want buttons and links to be 
          // inside TooltipWrapper, and not vice versa. 
          tabIndex={shouldHaveTabIndex ? 0 : undefined}
        >
          {props.children}
        </span>
      </span>
  );
}

export default TooltipWrapper;